import { useCallback, useContext, useEffect, useRef, useState } from 'react';
import { useLazyQuery } from '@apollo/client';
import { resolveBestChannel } from 'channel-selection';
import { LocationType } from 'lane-shared/types/LocationType';
import calculateChannelDistance from 'lane-shared/helpers/calculateChannelDistance';
import { TENANT_TYPES } from 'constants-channel';
import { FeatureFlag } from 'constants-flags';
import { isBuildingChannel, isTenantLocationChannel } from 'lane-shared/helpers/channel';
import { byDistance } from 'lane-shared/helpers/sort';
import Storage, { StorageKeys } from 'lane-shared/helpers/Storage';
import { Channel } from 'lane-shared/types/ChannelType';
import useFlag from 'lane-shared/hooks/useFlag';
import { convertTo62, convertToUUID } from 'uuid-encoding';
import { getChannelName } from 'graphql-queries';
import { AnalyticsContext } from 'lane-shared/contexts';
import { ANALYTIC_KEYS } from 'constants-analytics';
import emitter, { EVENT_INVALID_HOME_CHANNEL } from 'lane-shared/helpers/emitter';

export type UseBestDefaultChannelOptions = {
  isDeepLink?: boolean;
  isPostLogin?: boolean;
  onPostLoginRoutingResolved?: () => void;
};

function isSubTenantLocationChannel(channel: Channel) {
  return TENANT_TYPES.includes(channel.type) && channel.isSub && channel.parent;
}

function applyDistance(channel: Channel, location?: LocationType) {
  return {
    ...channel,
    distance: location
      ? calculateChannelDistance({ ...location, channel })
      : Number.MAX_SAFE_INTEGER,
  };
}

function sortByDistance(channels: Channel[], location?: LocationType) {
  return channels.map(channel => applyDistance(channel, location)).sort(byDistance);
}

function isInvalidHomeChannel<T extends { _id: string }>(
  channelId: string,
  channels: T[]
): boolean {
  return !channels.some(channel => channel._id === channelId);
}

function notifyPostLoginRoutingResolved(
  isPostLogin: boolean,
  onPostLoginRoutingResolved?: () => void
): void {
  if (isPostLogin && onPostLoginRoutingResolved) {
    onPostLoginRoutingResolved();
  }
}

function shouldTriggerAutoRevert(params: {
  channelId: string;
  channels: Channel[];
  lastReverted: string | null;
  isPostLogin: boolean;
  isDeepLink?: boolean;
  isHomeChannelSelectionEnabled: boolean;
}): boolean {
  const { channelId, channels, lastReverted, isPostLogin, isDeepLink, isHomeChannelSelectionEnabled } = params;
  return (
    isPostLogin &&
    !isDeepLink &&
    isHomeChannelSelectionEnabled &&
    channelId !== lastReverted &&
    isInvalidHomeChannel(channelId, channels)
  );
}

export function useBestDefaultChannel(
  channels: Channel[],
  location?: LocationType,
  defaultChannelIdFromUser?: string | null,
  options?: UseBestDefaultChannelOptions
) {
  const { isDeepLink, isPostLogin = false, onPostLoginRoutingResolved } =
    options ?? {};

  const [bestDefaultChannel, setBestDefaultChannel] = useState<Channel | null>(null);
  const fallbackToFirstChannel = useFlag(FeatureFlag.ReturnFirstChannelIfDefaultChannelIsNull, false);
  const isHomeChannelSelectionEnabled = useFlag(FeatureFlag.UserHomeChannelSelection, false);
  const analytics = useContext(AnalyticsContext);

  const [fetchChannel] = useLazyQuery<
    { channel?: { _id: string; name: string } | null },
    { id: string }
  >(getChannelName, { fetchPolicy: 'network-only' });

  const refs = useRef({ fetchChannel, analytics, defaultChannelIdFromUser });
  const hasAutoRevertedRef = useRef<string | null>(null);

  useEffect(() => {
    refs.current = { fetchChannel, analytics, defaultChannelIdFromUser };
  }, [fetchChannel, analytics, defaultChannelIdFromUser]);

  useEffect(() => {
    if (isPostLogin) {
      hasAutoRevertedRef.current = null;
    }
  }, [isPostLogin]);

  const onInvalidHomeChannel = useCallback((currentChannelName: string) => {
    const { defaultChannelIdFromUser: invalidChannelId, fetchChannel: fetchFn, analytics: analyticsInstance } = refs.current;

    const base62InvalidId = invalidChannelId != null ? convertTo62(invalidChannelId) : null;

    analyticsInstance.track(
      ANALYTIC_KEYS.ANALYTIC_HOME_CHANNEL_INVALID_PREFERENCE_DETECTED,
      { attemptedChannelId: base62InvalidId ?? undefined, action: 'auto_routed' },
    );

    const emitInvalidHomeChannel = (previousChannelName: string) => {
      emitter.emit(EVENT_INVALID_HOME_CHANNEL, { previousChannelName, invalidChannelId: base62InvalidId, currentChannelName });
    };

    if (!base62InvalidId) {
      emitInvalidHomeChannel('Unknown');

      return;
    }

    fetchFn({ variables: { id: convertToUUID(base62InvalidId) } })
      .then(({ data }) => { emitInvalidHomeChannel(data?.channel?.name ?? 'Unknown'); })
      .catch(() => { emitInvalidHomeChannel('Unknown'); });
  }, []);

  const channelIds = channels.map(channel => channel._id).join(',');

  useEffect(() => {
    if (isHomeChannelSelectionEnabled && !isDeepLink && defaultChannelIdFromUser === undefined) {
      setBestDefaultChannel(null);

      return;
    }

    if (channels.length === 0) {
      return;
    }

    notifyPostLoginRoutingResolved(isPostLogin, onPostLoginRoutingResolved);

    const base62DefaultChannelId =
      defaultChannelIdFromUser != null
        ? convertTo62(defaultChannelIdFromUser)
        : defaultChannelIdFromUser;

    const shouldRevert = Boolean(
      base62DefaultChannelId &&
      shouldTriggerAutoRevert({
        channelId: base62DefaultChannelId,
        channels,
        lastReverted: hasAutoRevertedRef.current,
        isPostLogin,
        isDeepLink,
        isHomeChannelSelectionEnabled: Boolean(isHomeChannelSelectionEnabled),
      })
    );

    if (shouldRevert && base62DefaultChannelId) {
      hasAutoRevertedRef.current = base62DefaultChannelId;
    }

    resolveBestChannel<Channel>({
      channels,
      defaultChannelIdFromUser: isDeepLink ? undefined : base62DefaultChannelId,
      isHomeChannelSelectionEnabled: Boolean(isHomeChannelSelectionEnabled),
      fallbackToFirstChannel: Boolean(fallbackToFirstChannel),
      getStoredValue: () => Storage.getItem(StorageKeys.PRIMARY_CHANNEL),
      isBuildingChannel: channel => Boolean(isBuildingChannel(channel)),
      isTenantLocationChannel: channel => Boolean(isTenantLocationChannel(channel)),
      isSubTenantLocationChannel: channel => Boolean(isSubTenantLocationChannel(channel)),
      sortByDistance: list => sortByDistance(list, location),
    }).then(resolved => {
      setBestDefaultChannel(resolved);

      if (shouldRevert) {
        onInvalidHomeChannel(resolved?.name ?? 'Unknown');
      }
    }).catch(error => {
      console.error('[home-channel] resolveBestChannel failed', error);

      if (shouldRevert) {
        onInvalidHomeChannel('Unknown');
      }
    });
  }, [
    channelIds,
    location,
    fallbackToFirstChannel,
    isHomeChannelSelectionEnabled,
    defaultChannelIdFromUser,
    isDeepLink,
    isPostLogin,
    onInvalidHomeChannel,
    onPostLoginRoutingResolved,
  ]);

  return bestDefaultChannel;
}
